/**
 * Iter 427.p34 — Shared SectionEditor + FieldEditor.
 *
 * Extracted from LeadOnboardingForm.jsx so the new Water Info Sheet
 * form (and any future onboarding-style form) renders with identical
 * visual pacing, spacing, chip styles, and voice-textarea behaviour.
 *
 * Field types (locked in with the existing schema): text | textarea |
 * number | date | select | multiselect | radio | upload
 */
import { X } from "lucide-react";
import VoiceTextarea from "../VoiceTextarea";
import DatePickerInput from "../DatePickerInput";

export function OnboardingSectionEditor({ section, data, setField, isLocked, testIdPrefix = "onboarding" }) {
  if (!section) return null;
  const get = (k) => data[`${section.key}.${k}`] ?? "";

  const shouldShow = (f) => {
    if (!f.show_if) return true;
    return Object.entries(f.show_if).every(([k, expected]) => {
      const v = get(k);
      if (Array.isArray(expected)) return expected.includes(v);
      return v === expected;
    });
  };

  return (
    <section
      className="hv-card p-4 md:p-6 space-y-5"
      data-testid={`${testIdPrefix}-section-${section.key}`}
    >
      <div>
        <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">
          {section.title}
        </h2>
        {section.description && (
          <div className="text-xs text-[#a0aabf] mt-1">{section.description}</div>
        )}
      </div>

      {(section.repeat_groups || []).map((g) => (
        <div
          key={g.prefix}
          className="border border-white/10 rounded p-3 md:p-4 space-y-3"
          data-testid={`${testIdPrefix}-group-${section.key}-${g.prefix}`}
        >
          <div className="text-xs uppercase tracking-wider text-[#1FB6C1] font-bold">
            {g.title}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {g.fields.map((f) => (
              <OnboardingFieldEditor
                key={`${g.prefix}_${f.key}`}
                field={{ ...f, key: `${g.prefix}_${f.key}` }}
                value={get(`${g.prefix}_${f.key}`)}
                onChange={(v) => setField(section.key, `${g.prefix}_${f.key}`, v)}
                disabled={isLocked}
                testIdPrefix={testIdPrefix}
              />
            ))}
          </div>
        </div>
      ))}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(section.fields || []).filter(shouldShow).map((f) => (
          <OnboardingFieldEditor
            key={f.key}
            field={f}
            value={get(f.key)}
            onChange={(v) => setField(section.key, f.key, v)}
            disabled={isLocked}
            testIdPrefix={testIdPrefix}
          />
        ))}
      </div>
    </section>
  );
}

export function OnboardingFieldEditor({ field, value, onChange, disabled, testIdPrefix = "onboarding" }) {
  const tid = `${testIdPrefix}-field-${field.key}`;
  const lab = (
    <label className="block text-[10px] uppercase tracking-wider text-[#647087] mb-1 font-bold">
      {field.label} {field.required && <span className="text-rose-400">*</span>}
    </label>
  );
  const wrap = (children, full = false) => (
    <div className={full ? "md:col-span-2" : ""}>
      {lab}
      {children}
    </div>
  );

  switch (field.type) {
    case "textarea":
      return wrap(
        <VoiceTextarea
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
          rows={4}
          placeholder={field.placeholder}
          className="text-sm w-full"
          context="onboarding_field"
          testIdPrefix={`${tid}-voice`}
          data-testid={tid}
        />,
        true,
      );
    case "number":
      return wrap(
        <input
          type="number"
          step="any"
          value={value === 0 ? 0 : value || ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          disabled={disabled}
          placeholder={field.placeholder}
          className="hv-input w-full text-sm"
          data-testid={tid}
        />,
      );
    case "date":
      return wrap(
        <DatePickerInput
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
          testId={tid}
        />,
      );
    case "select":
      return wrap(
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="hv-input w-full text-sm"
          data-testid={tid}
        >
          <option value="">— pick —</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>,
      );
    case "radio":
      return wrap(
        <div className="flex flex-wrap gap-2" data-testid={tid}>
          {field.options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(value === opt ? "" : opt)}
              disabled={disabled}
              className={`px-3 py-1.5 rounded border text-xs transition ${
                value === opt
                  ? "border-[#1FB6C1] bg-[#1FB6C1]/15 text-[#1FB6C1]"
                  : "border-white/10 text-[#a0aabf] hover:border-[#1FB6C1]/40"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid={`${tid}-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>,
      );
    case "multiselect": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (opt) =>
        arr.includes(opt)
          ? onChange(arr.filter((x) => x !== opt))
          : onChange([...arr, opt]);
      return wrap(
        <div className="flex flex-wrap gap-2" data-testid={tid}>
          {field.options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              disabled={disabled}
              className={`px-3 py-1.5 rounded border text-xs transition ${
                arr.includes(opt)
                  ? "border-[#A78BFA] bg-[#A78BFA]/15 text-[#A78BFA]"
                  : "border-white/10 text-[#a0aabf] hover:border-[#A78BFA]/40"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid={`${tid}-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>,
        true,
      );
    }
    case "upload": {
      // Iter 427.p34 — Simple file-attach chip. Stores an array of
      // { name, size, type, data_url } encoded client-side. Small
      // files only; large files should upload via the chat/appendix
      // path. Enforces a soft 5 MB per-file cap so a submitter can't
      // choke the payload.
      const arr = Array.isArray(value) ? value : [];
      const MAX_BYTES = 5 * 1024 * 1024;
      const onFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        const additions = [];
        for (const f of files) {
          if (f.size > MAX_BYTES) {
            // eslint-disable-next-line no-alert
            alert(`${f.name} is over 5 MB — please compress or upload via a chat message.`);
            continue;
          }
          const reader = new FileReader();
          const done = new Promise((res) => { reader.onload = () => res(reader.result); });
          reader.readAsDataURL(f);
          const dataUrl = await done;
          additions.push({ name: f.name, size: f.size, type: f.type, data_url: dataUrl });
        }
        if (additions.length) onChange([...arr, ...additions]);
      };
      return wrap(
        <div data-testid={tid} className="space-y-2">
          <input
            type="file"
            multiple={field.multiple !== false}
            onChange={onFiles}
            disabled={disabled}
            className="hv-input w-full text-xs"
            data-testid={`${tid}-input`}
          />
          {arr.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {arr.map((f, i) => (
                <span
                  key={`${f.name}-${i}`}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-white/15 bg-white/5 text-[#a0aabf]"
                  data-testid={`${tid}-file-${i}`}
                >
                  {f.name} · {Math.round((f.size || 0) / 1024)} kB
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => onChange(arr.filter((_, j) => j !== i))}
                      className="text-rose-300 hover:text-rose-200"
                      title="Remove"
                      data-testid={`${tid}-remove-${i}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>,
        true,
      );
    }
    case "text":
    default:
      return wrap(
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={field.placeholder}
          className="hv-input w-full text-sm"
          data-testid={tid}
        />,
      );
  }
}
